import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Paper,
  Typography,
} from '@mui/material'
import React, { useMemo, useState } from 'react'
import type { ExclusionAreaObservation } from '../../../../types/apiModels'
import { findingsTypes } from '../../../../utils/constants'
import { findingsTypesConventions } from '../../../../utils/findingColors'
import { ExclusionAreaObservationCard } from './ExclusionAreaObservationCard'

interface ExclusionAreaObservationsAccordionsProps {
  observations: ExclusionAreaObservation[]
  canResolve: boolean
  canComment: boolean
  onResolve: (observationId: string) => void
}

export const ExclusionAreaObservationsAccordions: React.FC<
  ExclusionAreaObservationsAccordionsProps
> = ({ observations, canResolve, canComment, onResolve }) => {
  const [expanded, setExpanded] = useState<string | false>(false)

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false)
  }

  const classified = useMemo(() => {
    const resolved: ExclusionAreaObservation[] = []
    const observed: ExclusionAreaObservation[] = []
    const toFix: ExclusionAreaObservation[] = []
    const severe: ExclusionAreaObservation[] = []

    observations.forEach(obs => {
      if (obs.resolved) {
        resolved.push(obs)
      } else {
        switch (obs.type) {
          case findingsTypes.Observation:
            observed.push(obs)
            break
          case findingsTypes.NonComplaintToFix:
            toFix.push(obs)
            break
          case findingsTypes.NonComplaintSevere:
            severe.push(obs)
            break
          default:
            break
        }
      }
    })

    return [
      { type: 'Observación', items: observed, color: findingsTypesConventions.observed.color },
      {
        type: 'No Conformidad a Corregir',
        items: toFix,
        color: findingsTypesConventions.nonComplaintToFix.color,
      },
      {
        type: 'No Conformidad Grave',
        items: severe,
        color: findingsTypesConventions.nonComplaintSevere.color,
      },
      { type: 'Resuelta', items: resolved, color: findingsTypesConventions.resolved.color },
    ]
  }, [observations])

  return (
    <>
      {classified.map(group => {
        if (group.items.length === 0) return null
        return (
          // Each card owns a comments query, so keeping collapsed groups mounted would fire one
          // request per observation the moment the modal opens; unmounting defers them to expand.
          <Accordion
            key={group.type}
            expanded={expanded === group.type}
            sx={{ width: '100%', boxShadow: 'none' }}
            TransitionProps={{ unmountOnExit: true }}
            onChange={handleChange(group.type)}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                margin: 0,
                minHeight: '40px',
                '& .MuiAccordionSummary-content': { margin: 0 },
              }}
            >
              <Typography sx={{ width: '50%', textAlign: 'start', fontSize: 16 }}>
                {group.type}
                <Badge
                  badgeContent={group.items.length}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: group.color,
                      color: 'white',
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      minWidth: '20px',
                      minHeight: '20px',
                      padding: '0 6px',
                      right: '-20px',
                    },
                  }}
                />
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Paper
                elevation={0}
                sx={{
                  padding: 1,
                  borderRadius: '5px 5px 5px 5px',
                  border: '1px solid #dcdcdc',
                  backgroundColor: 'inherit',
                }}
              >
                {group.items.map(obs => (
                  <ExclusionAreaObservationCard
                    key={obs.id}
                    canComment={canComment}
                    canResolve={canResolve}
                    observation={obs}
                    onResolve={onResolve}
                  />
                ))}
              </Paper>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </>
  )
}
